import React, { createContext, useContext, useState, useEffect } from 'react';

// Create context
const AppContext = createContext();

// Provider component
export const AppProvider = ({ children }) => {
  // Search state
  const [searchHistory, setSearchHistory] = useState([]);
  const [activeMode, setActiveMode] = useState('default');
  const [activeDepth, setActiveDepth] = useState('balanced');
  
  // User preferences
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [confidenceDisplay, setConfidenceDisplay] = useState(true);
  const [sourceDisplay, setSourceDisplay] = useState(true);
  
  // Initialize state from localStorage
  useEffect(() => {
    // Load history
    try {
      const savedHistory = localStorage.getItem('searchHistory');
      if (savedHistory) {
        setSearchHistory(JSON.parse(savedHistory));
      }
    } catch (error) {
      console.error('Error loading search history:', error);
    }
    
    // Load preferences
    try {
      const savedMode = localStorage.getItem('activeMode');
      if (savedMode) setActiveMode(savedMode);
      
      const savedDepth = localStorage.getItem('activeDepth');
      if (savedDepth) setActiveDepth(savedDepth);
      
      const savedDarkMode = localStorage.getItem('isDarkMode');
      if (savedDarkMode !== null) setIsDarkMode(JSON.parse(savedDarkMode));
      
      const savedConfidenceDisplay = localStorage.getItem('confidenceDisplay');
      if (savedConfidenceDisplay !== null) setConfidenceDisplay(JSON.parse(savedConfidenceDisplay));
      
      const savedSourceDisplay = localStorage.getItem('sourceDisplay');
      if (savedSourceDisplay !== null) setSourceDisplay(JSON.parse(savedSourceDisplay));
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  }, []);
  
  // Save to localStorage when state changes
  useEffect(() => {
    localStorage.setItem('searchHistory', JSON.stringify(searchHistory));
  }, [searchHistory]);
  
  useEffect(() => {
    localStorage.setItem('activeMode', activeMode);
  }, [activeMode]);
  
  useEffect(() => {
    localStorage.setItem('activeDepth', activeDepth);
  }, [activeDepth]);
  
  useEffect(() => {
    localStorage.setItem('isDarkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);
  
  useEffect(() => {
    localStorage.setItem('confidenceDisplay', JSON.stringify(confidenceDisplay));
  }, [confidenceDisplay]);
  
  useEffect(() => {
    localStorage.setItem('sourceDisplay', JSON.stringify(sourceDisplay));
  }, [sourceDisplay]);
  
  // Add to search history
  const addToSearchHistory = (query) => {
    const newHistoryItem = {
      id: Date.now().toString(),
      query,
      timestamp: Date.now()
    };
    
    setSearchHistory(prevHistory => {
      // Remove duplicates (if the same query exists, remove it first)
      const filteredHistory = prevHistory.filter(item => item.query !== query);
      // Add new item at the beginning
      return [newHistoryItem, ...filteredHistory].slice(0, 20); // Limit to 20 items
    });
  };
  
  // Clear search history
  const clearSearchHistory = () => {
    setSearchHistory([]);
  };
  
  // Toggle dark mode
  const toggleDarkMode = () => {
    setIsDarkMode(prev => !prev);
  };
  
  // Toggle confidence display
  const toggleConfidenceDisplay = () => {
    setConfidenceDisplay(prev => !prev);
  };
  
  // Toggle source display
  const toggleSourceDisplay = () => {
    setSourceDisplay(prev => !prev);
  };
  
  // Context value
  const contextValue = {
    // Search state
    searchHistory,
    activeMode,
    setActiveMode,
    activeDepth,
    setActiveDepth,
    
    // User preferences
    isDarkMode,
    confidenceDisplay,
    sourceDisplay,
    
    // Functions
    addToSearchHistory,
    clearSearchHistory,
    toggleDarkMode,
    toggleConfidenceDisplay,
    toggleSourceDisplay
  };
  
  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook for using the context
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export default AppContext;